import { useCallback, useEffect } from "react";
import { api } from "./api/client.js";
import { commandCenterApi, sessionAllows } from "./api/command-center.js";
import type { PermissionAction, RouteId } from "./api/types.js";
import { brand } from "./app/brand.js";
import {
  type AppRoute,
  isScanScopedPath,
  pathWithScanScope,
  preferredLandingScanId,
  scanScopeFromSearch,
  useAppRoute,
} from "./app/routing.js";
import { useTheme } from "./app/theme.js";
import { AppShell } from "./components/AppShell.js";
import { EmptyState, InlineError, LoadingTable } from "./components/AsyncState.js";
import { LicenceBanner } from "./components/LicenceBanner.js";
import { AdminPage } from "./features/admin/AdminPage.js";
import { AuditPage } from "./features/audit/AuditPage.js";
import { DeliveryPage } from "./features/delivery/DeliveryPage.js";
import { CoveragePage } from "./features/estate-coverage/CoveragePage.js";
import { FindingsPage } from "./features/findings/FindingsPage.js";
import { HelpPage } from "./features/help/HelpPage.js";
import { InventoryPage } from "./features/inventory/InventoryPage.js";
import { LandingPage } from "./features/landing/LandingPage.js";
import { OverviewPage } from "./features/overview/OverviewPage.js";
import { RemediationPage } from "./features/remediation/RemediationPage.js";
import { ReportsPage } from "./features/reports/ReportsPage.js";
import { ScansPage } from "./features/scans/ScansPage.js";
import { ProviderSettingsPage } from "./features/settings/ProviderSettings.js";
import { SourcesPage } from "./features/sources/SourcesPage.js";
import { ThreatModelsPage } from "./features/threat-models/ThreatModelsPage.js";
import { useApiResource } from "./hooks/useApiResource.js";

const ROUTE_ACTIONS: Partial<Record<RouteId, PermissionAction>> = {
  overview: "overview:read",
  findings: "findings:read",
  coverage: "overview:read",
  inventory: "overview:read",
  reports: "reports:read",
  scans: "scans:read",
  sources: "sources:read",
  delivery: "delivery:read",
  remediation: "remediation:read",
  "threat-models": "reports:read",
  audit: "audit:read",
  settings: "providers:read",
  admin: "admin:read",
  help: "help:read",
};

export function requiredActionForRoute(routeId: RouteId): PermissionAction {
  return ROUTE_ACTIONS[routeId] ?? "overview:read";
}

export function App() {
  const { route, navigate } = useAppRoute();
  const { preference, setPreference } = useTheme();
  useEffect(() => {
    document.title = route.id === "landing" ? `${brand.name} — Application security evidence and review` : brand.name;
  }, [route.id]);

  if (route.id === "landing") return <LandingPage navigate={navigate} theme={preference} onThemeChange={setPreference} />;

  return (
    <ConsoleApp
      route={route}
      navigate={navigate}
      preference={preference}
      setPreference={setPreference}
    />
  );
}

function ConsoleApp({
  route,
  navigate,
  preference,
  setPreference,
}: {
  route: AppRoute;
  navigate: (path: string) => void;
  preference: ReturnType<typeof useTheme>["preference"];
  setPreference: ReturnType<typeof useTheme>["setPreference"];
}) {
  const scopedRoute = isScanScopedPath(route.path);
  const requestedScope = scanScopeFromSearch(route.search);
  const loadScans = useCallback(
    (signal: AbortSignal) => api.getScans({ page: 1, pageSize: 500 }, signal),
    [],
  );
  const scans = useApiResource(loadScans);
  const landingScanId = preferredLandingScanId(scans.data?.items);
  useEffect(() => {
    if (!scopedRoute || requestedScope.allScans || requestedScope.scanId || !landingScanId) return;
    navigate(pathWithScanScope(`${route.path}${route.search}`, landingScanId));
  }, [
    landingScanId,
    navigate,
    requestedScope.allScans,
    requestedScope.scanId,
    route.path,
    route.search,
    scopedRoute,
  ]);
  const effectiveScanId = scopedRoute
    ? requestedScope.allScans
      ? undefined
      : (requestedScope.scanId ?? landingScanId)
    : undefined;
  const shellScanId = requestedScope.allScans
    ? undefined
    : (requestedScope.scanId ?? landingScanId);
  const allScans = requestedScope.allScans;
  const changeScanScope = useCallback(
    (scanId: string | undefined) => {
      navigate(pathWithScanScope(`${route.path}${route.search}`, scanId, !scanId));
    },
    [navigate, route.path, route.search],
  );
  const loadSummary = useCallback(
    (signal: AbortSignal) => commandCenterApi.getStatus(effectiveScanId, signal),
    [effectiveScanId],
  );
  const loadSession = useCallback((signal: AbortSignal) => commandCenterApi.getSession(signal), []);
  const summary = useApiResource(loadSummary);
  const session = useApiResource(loadSession);

  const authorized =
    route.id === "not-found" ||
    (session.data && sessionAllows(session.data, requiredActionForRoute(route.id)));

  const routePage =
    route.id === "overview" && session.data ? (
      <OverviewPage navigate={navigate} selectedScanId={effectiveScanId} />
    ) : route.id === "findings" ? (
      <FindingsPage
        initialSearch={route.search}
        selectedScanId={effectiveScanId}
        principalId={session.data?.principalId ?? "unknown"}
        tenantId={session.data?.tenantId ?? "unknown"}
        navigate={navigate}
        onScanChange={changeScanScope}
        canRemediate={sessionAllows(session.data, "remediation:write")}
        canReview={sessionAllows(session.data, "findings:approve")}
      />
    ) : route.id === "coverage" && session.data ? (
      <CoveragePage session={session.data} navigate={navigate} selectedScanId={effectiveScanId} />
    ) : route.id === "inventory" ? (
      <InventoryPage key={effectiveScanId ?? "portfolio"} selectedScanId={effectiveScanId} />
    ) : route.id === "reports" && session.data ? (
      <ReportsPage
        selectedScanId={effectiveScanId}
        canDownload={sessionAllows(session.data, "reports:download")}
      />
    ) : route.id === "scans" ? (
      <ScansPage
        canStartScan={
          sessionAllows(session.data, "scans:write") && (session.data?.licence?.canStartScans ?? true)
        }
        initialSearch={route.search}
        onOpenSources={() => navigate("/sources")}
        onOpenFindingsForScan={(scanId) =>
          navigate(`/findings?scanId=${encodeURIComponent(scanId)}&preset=actionable`)
        }
      />
    ) : route.id === "sources" ? (
      <SourcesPage
        canManage={
          sessionAllows(session.data, "sources:write") &&
          (session.data?.licence
            ? session.data.licence.canAddSources || session.data.licence.canStartScans
            : true)
        }
        canManageBusinessTier={
          session.data?.role === "owner" || session.data?.role === "risk_manager"
        }
        onOpenScans={() => navigate("/scans")}
      />
    ) : route.id === "audit" ? (
      <AuditPage />
    ) : route.id === "delivery" ? (
      <DeliveryPage />
    ) : route.id === "remediation" ? (
      <RemediationPage
        canWrite={sessionAllows(session.data, "remediation:write")}
        canApprove={sessionAllows(session.data, "remediation:approve")}
        initialFindingId={new URLSearchParams(route.search).get("findingId") ?? undefined}
        initialRepositoryId={new URLSearchParams(route.search).get("repositoryId") ?? undefined}
      />
    ) : route.id === "threat-models" ? (
      <ThreatModelsPage
        canEdit={session.data?.role === "owner" || session.data?.role === "risk_manager"}
      />
    ) : route.id === "settings" ? (
      <ProviderSettingsPage />
    ) : route.id === "admin" ? (
      <AdminPage theme={preference} onThemeChange={setPreference} />
    ) : route.id === "help" ? (
      <HelpPage />
    ) : (
      <EmptyState
        title="Page not found"
        description="This console route does not exist. Use the primary navigation to continue."
        action={
          <button className="button" type="button" onClick={() => navigate("/overview")}>
            Open overview
          </button>
        }
      />
    );

  const page =
    session.isLoading && !session.data ? (
      <LoadingTable columns={3} rows={4} />
    ) : session.error && !session.data ? (
      <InlineError error={session.error} onRetry={session.refresh} />
    ) : authorized ? (
      routePage
    ) : (
      <EmptyState
        title="This route is outside your scope"
        description="Your current tenant role does not advertise access to this route. The API remains the authorization authority."
        action={
          <button className="button" type="button" onClick={() => navigate("/overview")}>
            Open overview
          </button>
        }
      />
    );

  return (
    <AppShell
      route={route}
      navigate={navigate}
      theme={preference}
      setTheme={setPreference}
      summary={summary}
      selectedScanId={shellScanId}
      allScans={allScans}
      latestScanId={landingScanId}
      scanNamedOptions={(scans.data?.items ?? []).map((scan) => ({
        value: scan.id,
        label: `${scan.repositoryName} · ${scan.status} · ${new Date(scan.startedAt).toLocaleString()}`,
      }))}
      session={session}
    >
      <LicenceBanner licence={session.data?.licence} />
      {page}
    </AppShell>
  );
}
